import React, { useEffect, useRef, useState } from 'react';

import { Button, Input, Modal } from '@copilot/ui';
import { Field } from '../common/Field';
import DocumentTable from './DocumentTable';
import fitDocxToWidth from './fitDocxToWidth';
import * as documentService from '../../services/documentService';
import { reportError } from '../../services/errorTracking';
import { DOCUMENT_EXTENSIONS } from '../../utils/documentTypes';
import { getUserFriendlyError } from '../../utils/errors';
import parseCsv from '../../utils/parseCsv';

// A very large text file in one <pre> can freeze the tab, so only this
// much is drawn; Download has the whole file, and editing (TXT) always
// works on the full text, never the truncated view.
const MAX_DISPLAY_CHARS = 1_000_000;

// Likewise a CSV table: only this many rows (the header included) are parsed
// and drawn; Download has the whole file.
const MAX_TABLE_ROWS = 1000;

// The most rows per sheet the backend returns for a workbook (see
// PREVIEW_MAX_ROWS in backend/services/document_processor.py).
const MAX_SHEET_ROWS = 1000;

// Mirrors backend/schemas/document.py's ManualDocumentCreate title limit.
const MAX_TITLE_LENGTH = 255;

// Shown instead of the original layout when a DOCX/PPTX cannot be rendered:
// the text the backend extracted from it, so the document is still readable.
async function fallBackToExtractedText({ documentId, blob, isCancelled, setView }) {
  try {
    const data = await documentService.getDocumentContent(documentId);
    if (!isCancelled()) {
      setView({
        kind: 'text',
        text: data.content ?? null,
        blob,
        notice:
          'This document could not be shown in its original layout, so its text is shown instead.',
      });
    }
  } catch (err) {
    if (!isCancelled()) {
      setView({
        kind: 'error',
        message: getUserFriendlyError(err, 'Could not load this document.'),
      });
    }
  }
}

function emptyContentMessage(document) {
  if (['pending', 'processing'].includes(document.processing_status)) {
    return 'This document is still being processed. Try again in a moment.';
  }
  if (document.processing_status === 'failed') {
    return 'No text could be extracted from this document.';
  }
  return 'This document has no text content.';
}

/**
 * Shows a document the way it was added: an uploaded PDF in the
 * browser's own PDF viewer, an uploaded DOCX rendered as a document
 * (docx-preview), an uploaded PPTX as its slides (pptx-preview), a CSV
 * as a table, an XLSX as a table per sheet (read by the backend), and
 * TXT / manual documents as plain text. The
 * original file is fetched from GET /api/documents/{id}/file; anything
 * that cannot be shown that way (a DOCX docx-preview cannot read)
 * falls back to the text the backend extracted from it
 * (GET /api/documents/{id}/content).
 *
 * A manual document can also be edited here (title and text), and so
 * can an uploaded .txt file (text only - the file keeps its name);
 * saving hands the updated document to onSaved so the parent can
 * refresh its list. PDF and DOCX uploads are never editable - those are
 * binary formats the app cannot write back.
 *
 * The parent renders this only while a document is open, so all state
 * here belongs to exactly one document and is torn down - blob URL
 * revoked, in-flight loads ignored - when it closes.
 */
function DocumentViewer({ document, onClose, onSaved }) {
  const [view, setView] = useState({ kind: 'loading' });
  const docxContainerRef = useRef(null);
  const pptxContainerRef = useRef(null);

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [activeSheet, setActiveSheet] = useState(0);

  const isUpload =
    document.type !== 'manual' && DOCUMENT_EXTENSIONS.includes(document.file_type);
  const isTextUpload = isUpload && document.file_type === 'txt';

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    async function load() {
      try {
        if (!isUpload) {
          const data = await documentService.getDocumentContent(document.id);
          if (!cancelled) setView({ kind: 'text', text: data.content ?? null });
          return;
        }

        if (document.file_type === 'xlsx') {
          const { sheets } = await documentService.getDocumentSheets(document.id);
          if (!cancelled) setView({ kind: 'sheets', sheets });
          return;
        }

        const blob = await documentService.getDocumentFile(document.id);
        if (cancelled) return;

        if (document.file_type === 'pdf') {
          // The browser's PDF viewer needs the blob typed as a PDF.
          const pdf =
            blob.type === 'application/pdf' ? blob : blob.slice(0, blob.size, 'application/pdf');
          objectUrl = URL.createObjectURL(pdf);
          setView({ kind: 'pdf', url: objectUrl, blob });
        } else if (document.file_type === 'docx') {
          setView({ kind: 'docx', blob });
        } else if (document.file_type === 'pptx') {
          setView({ kind: 'pptx', blob });
        } else if (document.file_type === 'csv') {
          const { rows, truncated } = parseCsv(await blob.text(), { maxRows: MAX_TABLE_ROWS });
          setView({ kind: 'table', rows, truncated, blob });
        } else {
          setView({ kind: 'text', text: await blob.text(), blob });
        }
      } catch (err) {
        if (cancelled) return;
        reportError('document_view', err);
        setView({
          kind: 'error',
          message: getUserFriendlyError(err, 'Could not load this document.'),
        });
      }
    }

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document.id, document.file_type, isUpload]);

  // A DOCX renders into a container that only exists once the view
  // flips to 'docx', so it gets its own effect. docx-preview is loaded
  // on demand: most sessions never open a Word document, and it should
  // not weigh down the main bundle.
  const docxBlob = view.kind === 'docx' ? view.blob : null;

  useEffect(() => {
    if (!docxBlob || !docxContainerRef.current) return undefined;

    let cancelled = false;
    let resizeObserver = null;
    const container = docxContainerRef.current;

    import('docx-preview')
      .then(async ({ renderAsync }) => {
        await renderAsync(docxBlob, container, undefined, { inWrapper: true });
        if (cancelled) return;

        // Pages render at their real width, which is usually wider than
        // this dialog - scale them to fit, and keep fitting if it resizes.
        fitDocxToWidth(container);
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => fitDocxToWidth(container));
          resizeObserver.observe(container);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        reportError('document_docx_render', err);
        return fallBackToExtractedText({
          documentId: document.id,
          blob: docxBlob,
          isCancelled: () => cancelled,
          setView,
        });
      });

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      container.innerHTML = '';
    };
  }, [docxBlob, document.id]);

  // Same shape as the DOCX effect above. pptx-preview draws every slide
  // (list mode) at the container's width, keeping each slide's own aspect
  // ratio, and it and its charting dependency are loaded on demand.
  const pptxBlob = view.kind === 'pptx' ? view.blob : null;

  useEffect(() => {
    if (!pptxBlob || !pptxContainerRef.current) return undefined;

    let cancelled = false;
    let previewer = null;
    const container = pptxContainerRef.current;

    Promise.all([import('pptx-preview'), pptxBlob.arrayBuffer()])
      .then(async ([{ init }, buffer]) => {
        // Loaded here rather than at the top: like pptx-preview it is only
        // needed once a deck is actually opened.
        const { default: repairPptx } = await import('./repairPptx');
        const repaired = await repairPptx(buffer);
        if (cancelled) return;

        previewer = init(container, { width: container.clientWidth || 720, mode: 'list' });
        const presentation = await previewer.preview(repaired);

        // pptx-preview swallows some parse errors and resolves with an
        // empty deck, which would leave a blank dialog.
        if (!presentation?.slides?.length) {
          throw new Error('The presentation has no readable slides.');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        reportError('document_pptx_render', err);
        return fallBackToExtractedText({
          documentId: document.id,
          blob: pptxBlob,
          isCancelled: () => cancelled,
          setView,
        });
      });

    return () => {
      cancelled = true;
      if (previewer) previewer.destroy();
      container.innerHTML = '';
    };
  }, [pptxBlob, document.id]);

  // Editing needs the loaded text, and the backend refuses an edit
  // while the previous text is still being indexed.
  const canEdit =
    (!isUpload || isTextUpload) &&
    view.kind === 'text' &&
    !['pending', 'processing'].includes(document.processing_status);

  function startEditing() {
    setDraftTitle(document.file_name);
    setDraftContent(view.text || '');
    setSaveError('');
    setEditing(true);
  }

  function cancelEditing() {
    if (isSaving) return;
    setEditing(false);
    setSaveError('');
  }

  async function handleSave(event) {
    event?.preventDefault();
    if (isSaving) return;

    const title = draftTitle.trim();
    // A text file keeps its exact whitespace; a manual document is
    // trimmed, like when it is first created.
    const content = isTextUpload ? draftContent : draftContent.trim();

    if (!isTextUpload && !title) {
      setSaveError('Title is required.');
      return;
    }
    if (!content.trim()) {
      setSaveError('Content is required.');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const updated = isTextUpload
        ? await documentService.updateTextDocument(document.id, content)
        : await documentService.updateManualDocument(document.id, { title, content });
      setView({
        kind: 'text',
        text: updated.content,
        // So Download hands back the edited text, not the old file.
        blob: isTextUpload ? new Blob([updated.content], { type: 'text/plain' }) : undefined,
      });
      setEditing(false);
      onSaved?.(updated);
    } catch (err) {
      reportError('manual_document_update', err);
      setSaveError(getUserFriendlyError(err, 'Could not save changes.'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    if (isSaving) return;
    onClose();
  }

  // The PDF viewer has its own download button in its toolbar; every
  // other uploaded file gets one here.
  const canDownload = isUpload && !['pdf', 'loading', 'error'].includes(view.kind);

  async function handleDownload() {
    setDownloadError('');

    let blob = view.blob;
    if (!blob) {
      // Views built from extracted text never fetched the original file.
      try {
        blob = await documentService.getDocumentFile(document.id);
      } catch (err) {
        reportError('document_download', err);
        setDownloadError(getUserFriendlyError(err, 'Could not download this document.'));
        return;
      }
    }

    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.file_name;
    link.click();
    URL.revokeObjectURL(url);
  }

  let body;
  if (editing) {
    body = (
      <form className="manual-document-form" onSubmit={handleSave}>
        {!isTextUpload && (
          <Field label="Title" htmlFor="document-viewer-title">
            <Input
              id="document-viewer-title"
              value={draftTitle}
              maxLength={MAX_TITLE_LENGTH}
              onChange={(event) => setDraftTitle(event.target.value)}
              disabled={isSaving}
            />
          </Field>
        )}

        <Field label="Content" htmlFor="document-viewer-content">
          <textarea
            id="document-viewer-content"
            className="input manual-document-textarea document-edit-textarea"
            value={draftContent}
            onChange={(event) => setDraftContent(event.target.value)}
            disabled={isSaving}
          />
        </Field>

        {saveError && <div className="form-alert">{saveError}</div>}
      </form>
    );
  } else if (view.kind === 'loading') {
    body = <p className="backend-document-meta">Loading document...</p>;
  } else if (view.kind === 'error') {
    body = <div className="form-alert">{view.message}</div>;
  } else if (view.kind === 'pdf') {
    body = <iframe className="document-pdf-view" src={view.url} title={document.file_name} />;
  } else if (view.kind === 'docx') {
    body = <div className="document-docx-view" ref={docxContainerRef} />;
  } else if (view.kind === 'pptx') {
    body = <div className="document-pptx-view" ref={pptxContainerRef} />;
  } else if (view.kind === 'table') {
    body = (
      <DocumentTable
        rows={view.rows}
        truncated={view.truncated}
        limit={MAX_TABLE_ROWS}
        emptyMessage="This file has no rows."
      />
    );
  } else if (view.kind === 'sheets') {
    const sheet = view.sheets[Math.min(activeSheet, view.sheets.length - 1)];

    body = sheet ? (
      <>
        <div className="document-sheet-tabs" role="tablist" aria-label="Sheets">
          {view.sheets.map((candidate, index) => (
            <button
              // Sheet names are unique within a workbook.
              key={candidate.name}
              type="button"
              role="tab"
              aria-selected={candidate === sheet}
              className={`document-sheet-tab${candidate === sheet ? ' is-active' : ''}`}
              onClick={() => setActiveSheet(index)}
            >
              {candidate.name}
            </button>
          ))}
        </div>
        <DocumentTable
          rows={sheet.rows}
          truncated={sheet.truncated}
          limit={MAX_SHEET_ROWS}
          emptyMessage="This sheet is empty."
        />
      </>
    ) : (
      <p className="backend-document-meta">This workbook has no sheets.</p>
    );
  } else {
    body = (
      <>
        {view.text && view.notice && <p className="backend-document-meta">{view.notice}</p>}
        {view.text && view.text.length > MAX_DISPLAY_CHARS && (
          <p className="backend-document-meta">
            This file is very large, so only the first part is shown here.
            {isUpload ? ' Download it to see everything.' : ''}
          </p>
        )}
        {view.text ? (
          <pre className="document-content-view">
            {view.text.length > MAX_DISPLAY_CHARS
              ? view.text.slice(0, MAX_DISPLAY_CHARS)
              : view.text}
          </pre>
        ) : (
          <p className="backend-document-meta">{emptyContentMessage(document)}</p>
        )}
      </>
    );
  }

  let actions = null;
  if (editing) {
    actions = (
      <>
        <Button variant="secondary" onClick={cancelEditing} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </>
    );
  } else if (canEdit || canDownload) {
    actions = (
      <>
        {canEdit && (
          <Button variant="secondary" onClick={startEditing}>
            Edit
          </Button>
        )}
        {canDownload && (
          <Button variant="secondary" onClick={handleDownload}>
            Download
          </Button>
        )}
      </>
    );
  }

  return (
    <Modal title={document.file_name} size="lg" onClose={handleClose} actions={actions}>
      {downloadError && !editing && <div className="form-alert">{downloadError}</div>}
      {body}
    </Modal>
  );
}

export default DocumentViewer;
